/**
 * GanttChart — кастомная диаграмма Ганта с «замороженными» левой панелью и шапкой.
 *
 * Структура данных от бэкенда:
 *   type='project' → строка-заголовок проекта (тёмно-синий бар)
 *   type='section' → строки разделов (цветные бары)
 *
 * Особенности:
 *   • sticky left-панель (280 px) + sticky top-шапка (72 px = 3 × 24 px)
 *   • Шапка: год → месяц → декада (I / II / III)
 *   • «Сегодня» центрируется при монтировании; красная вертикальная линия
 *   • Все проекты свёрнуты по умолчанию
 *   • Tooltip: при наведении на бар — даты с годом, ГИП / исполнитель
 */
import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { dashboardApi } from '@/api/dashboard'
import { useEffectiveRole } from '@/store/auth'
import Spinner from '@/components/common/Spinner'
import type { GanttTask, ProjectLifecycleStage } from '@/types'

// ─── Константы ────────────────────────────────────────────────────────────────
const LEFT_W      = 280   // ширина левой панели, px
const PX_PER_DAY  = 3.5  // пикселей на календарный день
const ROW_H_PROJ  = 46   // высота строки проекта, px (было 56)
const ROW_H_GROUP = 34   // высота строки группы (ИРД/ПД/РД/ГГЭ), px (было 44)
const ROW_H_SEC   = 30   // высота строки раздела, px (было 40)
const ROW_H_STAGE = 24   // высота строки этапа раздела, px
const HDR_H       = 72   // высота шапки (3 × 24 px)

const BAR_COLOR: Record<string, string> = {
  'bar-project': '#1e3a8a',
  'bar-group':   '#4f46e5',  // indigo для групп
  'bar-green':   '#10b981',
  'bar-yellow':  '#f59e0b',
  'bar-red':     '#ef4444',
  'bar-grey':    '#9ca3af',
}

// Палитра состояния сроков (status_color от бэкенда)
const STATUS_BAR: Record<string, string> = {
  red:    '#ef4444',
  yellow: '#f59e0b',
  green:  '#10b981',
  grey:   '#9ca3af',
}

/**
 * Цвет временной линии (бара) по состоянию сроков.
 * Проект/группа сохраняют «фирменный» цвет, только когда всё в срок (green);
 * при просрочке/приближении — становятся красными/жёлтыми. Раздел — всегда по статусу.
 */
function barColor(task: GanttTask): string {
  const sc = task.status_color
  if (task.type === 'project') {
    return sc && sc !== 'green' ? STATUS_BAR[sc] : BAR_COLOR['bar-project']
  }
  if (task.type === 'doc_group') {
    return sc && sc !== 'green' ? STATUS_BAR[sc] : BAR_COLOR['bar-group']
  }
  return (sc && STATUS_BAR[sc]) ?? BAR_COLOR[task.custom_class] ?? '#9ca3af'
}

// Цвета зебры для групп проектов (чётные / нечётные)
const PROJ_BG_EVEN  = '#dbeafe'
const PROJ_BG_ODD   = '#ede9fe'
const GROUP_BG_EVEN = '#e0e7ff'
const GROUP_BG_ODD  = '#ede9fe'
const SEC_BG_EVEN   = '#f0f9ff'
const SEC_BG_ODD    = '#faf5ff'

// ─── Вспомогательные функции ─────────────────────────────────────────────────
function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** dd.mm.yyyy */
function fmtDate(s: string): string {
  const [y, m, d] = s.split('-')
  return `${d}.${m}.${y}`
}

const MONTH_SHORT = ['Янв','Фев','Мар','Апр','Май','Июн','Июл','Авг','Сен','Окт','Ноя','Дек']

interface HItem { x: number; w: number; label: string }

interface Header {
  years: HItem[]
  months: HItem[]
  decades: HItem[]
  totalW: number
}

/**
 * Строит списки ячеек для трёх рядов шапки.
 * rs — начало диапазона (всегда 1-е число месяца),
 * re — конец диапазона (1-е число следующего месяца).
 */
function buildHeader(rs: Date, re: Date): Header {
  const totalDays = daysBetween(rs, re)
  const totalW    = totalDays * PX_PER_DAY
  const years:   HItem[] = []
  const months:  HItem[] = []
  const decades: HItem[] = []

  let cur     = new Date(rs.getFullYear(), rs.getMonth(), 1)
  let curYear = -1

  while (cur < re) {
    const nextMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
    const yr        = cur.getFullYear()

    // Ряд месяцев
    const mX   = Math.max(0, daysBetween(rs, cur)) * PX_PER_DAY
    const mEnd = Math.min(totalW, daysBetween(rs, nextMonth) * PX_PER_DAY)
    if (mEnd > mX) months.push({ x: mX, w: mEnd - mX, label: MONTH_SHORT[cur.getMonth()] })

    // Ряд годов
    if (yr !== curYear) {
      if (years.length) years[years.length - 1].w = mX - years[years.length - 1].x
      curYear = yr
      years.push({ x: mX, w: 0, label: String(yr) })
    }

    // Ряд декад: I (1–10), II (11–20), III (21–конец месяца)
    const decStarts = [1, 11, 21]
    for (let di = 0; di < 3; di++) {
      const ds   = new Date(cur.getFullYear(), cur.getMonth(), decStarts[di])
      const de   = di < 2
        ? new Date(cur.getFullYear(), cur.getMonth(), decStarts[di + 1])
        : nextMonth
      const dX   = daysBetween(rs, ds) * PX_PER_DAY
      const dEnd = daysBetween(rs, de) * PX_PER_DAY
      const cX   = Math.max(0, dX)
      const cEnd = Math.min(totalW, dEnd)
      if (cEnd > cX) decades.push({ x: cX, w: cEnd - cX, label: ['I','II','III'][di] })
    }

    cur = nextMonth
  }

  if (years.length) years[years.length - 1].w = totalW - years[years.length - 1].x

  return { years, months, decades, totalW }
}

// ─── Компонент ────────────────────────────────────────────────────────────────
interface Props {
  stage?:     ProjectLifecycleStage
  title?:     string
  anchorId?:  string
  projectId?: string   // ограничить одним проектом (карточка проекта)
}

interface TipState {
  visible: boolean
  x:       number
  y:       number
  task:    GanttTask | null
}

export default function GanttChart({ stage, title = 'Диаграмма Ганта', anchorId, projectId }: Props) {
  const outerRef   = useRef<HTMLDivElement>(null)
  const didScroll  = useRef(false)
  const navigate   = useNavigate()
  const role       = useEffectiveRole()
  // Стоимости разделов видят только директор и замдир
  const showCost   = role === 'director' || role === 'deputy_director'

  const [collapsedProjs,    setCollapsedProjs]    = useState<Set<string>>(new Set())
  const [collapsedGroups,   setCollapsedGroups]   = useState<Set<string>>(new Set())
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const [tip, setTip]             = useState<TipState>({ visible: false, x: 0, y: 0, task: null })

  // ── Данные ──────────────────────────────────────────────────────────────────
  const { data: all, isLoading, error } = useQuery({
    queryKey: ['gantt', stage ?? 'all', projectId ?? 'global'],
    queryFn:  () => dashboardApi.gantt(stage, projectId).then(r => r.data),
    staleTime: 20_000,
    // Чужие правки сроков появляются на Ганте без перезагрузки страницы
    refetchInterval: 20_000,
  })

  // При загрузке данных: в карточке проекта (projectId) — всё развёрнуто;
  // на общем экране — проекты свёрнуты, группы развёрнуты.
  // Разделы с этапами — всегда свёрнуты по умолчанию (этапы скрыты до клика).
  useEffect(() => {
    if (!all) return
    setCollapsedProjs(
      projectId ? new Set() : new Set(all.filter(t => t.type === 'project').map(t => t.project_id))
    )
    setCollapsedGroups(new Set())
    // Сворачиваем все разделы у которых есть этапы
    const sectionsWithStages = new Set(
      all.filter(t => t.type === 'stage' && t.section_id).map(t => t.section_id!)
    )
    setCollapsedSections(sectionsWithStages)
    didScroll.current = false
  }, [all, projectId])

  // ── Диапазон дат ────────────────────────────────────────────────────────────
  const { rs, re } = useMemo(() => {
    const validAll = (all ?? []).filter(t => t.start && t.end)
    if (!validAll.length) {
      const t = new Date(); t.setDate(1)
      return { rs: t, re: new Date(t.getFullYear(), t.getMonth() + 3, 1) }
    }
    let mn = parseDate(validAll[0].start), mx = parseDate(validAll[0].end)
    for (const t of validAll) {
      const s = parseDate(t.start), e = parseDate(t.end)
      if (s < mn) mn = s
      if (e > mx) mx = e
    }
    // Отступ 14 дней + привязка к началу/концу месяца
    const lo = new Date(mn); lo.setDate(lo.getDate() - 14)
    const hi = new Date(mx); hi.setDate(hi.getDate() + 14)
    return {
      rs: new Date(lo.getFullYear(), lo.getMonth(),     1),
      re: new Date(hi.getFullYear(), hi.getMonth() + 1, 1),
    }
  }, [all])

  const hdr      = useMemo(() => buildHeader(rs, re), [rs, re])

  // X-координата «Сегодня» в px от начала временной шкалы
  const todayX   = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0)
    return daysBetween(rs, t) * PX_PER_DAY
  }, [rs])

  // Подпись даты рядом с линией «Сегодня»
  const todayLabel = useMemo(() => {
    const t = new Date()
    const d = String(t.getDate()).padStart(2, '0')
    const m = String(t.getMonth() + 1).padStart(2, '0')
    return `${d}.${m}.${t.getFullYear()}`
  }, [])

  // Только видимые строки (4-уровневая логика collapse: проект → группа → раздел → этап)
  const visible = useMemo(() => {
    return (all ?? []).filter(t => {
      if (t.type === 'project') return true
      if (collapsedProjs.has(t.project_id)) return false
      if (t.type === 'doc_group') return true
      const groupKey = `${t.project_id}_${t.doc_type}`
      if (collapsedGroups.has(groupKey)) return false
      if (t.type === 'section') return true
      // stage: скрыт если свёрнут родительский раздел
      return !collapsedSections.has(t.section_id ?? '')
    })
  }, [all, collapsedProjs, collapsedGroups, collapsedSections])

  // Индекс группы (для зебры) — per project_id
  const projGroupIdx = useMemo(() => {
    const map = new Map<string, number>()
    let idx = 0
    for (const t of (all ?? [])) {
      if (t.type === 'project' && !map.has(t.project_id)) map.set(t.project_id, idx++)
    }
    return map
  }, [all])

  // Геометрия баров (предвычисляем, чтобы не вызывать parseDate в рендере)
  const barGeom = useMemo(() => visible.map(task => {
    if (!task.start || !task.end) return { bX: 0, bW: 0, hasBar: false }
    const bX   = Math.max(0, daysBetween(rs, parseDate(task.start))) * PX_PER_DAY
    const bEnd = Math.min(hdr.totalW, (daysBetween(rs, parseDate(task.end)) + 1) * PX_PER_DAY)
    const bW   = bEnd - bX
    return { bX, bW: bW > 0 ? Math.max(6, bW) : 0, hasBar: bW > 0 }
  }), [visible, rs, hdr.totalW])

  // ── Прокрутка к «Сегодня» ───────────────────────────────────────────────────
  useEffect(() => {
    if (!all || !outerRef.current || didScroll.current) return
    const cW = outerRef.current.clientWidth
    outerRef.current.scrollLeft = Math.max(0, todayX - (cW - LEFT_W) / 2)
    didScroll.current = true
  }, [all, todayX])

  const scrollToToday = useCallback(() => {
    if (!outerRef.current) return
    const cW = outerRef.current.clientWidth
    outerRef.current.scrollTo({ left: Math.max(0, todayX - (cW - LEFT_W) / 2), behavior: 'smooth' })
  }, [todayX])

  // Набор id разделов, у которых есть этапы (для отображения стрелки свёртывания)
  const sectionsWithStages = useMemo(() => {
    const set = new Set<string>()
    for (const t of (all ?? [])) {
      if (t.type === 'stage' && t.section_id) set.add(t.section_id)
    }
    return set
  }, [all])

  // ── Коллапс ─────────────────────────────────────────────────────────────────
  const toggleProj = useCallback((pid: string) => {
    setCollapsedProjs(prev => {
      const n = new Set(prev)
      n.has(pid) ? n.delete(pid) : n.add(pid)
      return n
    })
  }, [])

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const n = new Set(prev)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })
  }, [])

  const toggleSection = useCallback((secId: string) => {
    setCollapsedSections(prev => {
      const n = new Set(prev)
      n.has(secId) ? n.delete(secId) : n.add(secId)
      return n
    })
  }, [])

  // ── Tooltip ─────────────────────────────────────────────────────────────────
  const onBarEnter = useCallback((e: React.MouseEvent, task: GanttTask) => {
    setTip({ visible: true, x: e.clientX, y: e.clientY, task })
  }, [])
  const onBarLeave = useCallback(() => setTip(p => ({ ...p, visible: false })), [])
  const onBarMove  = useCallback((e: React.MouseEvent) => {
    setTip(p => p.visible ? { ...p, x: e.clientX, y: e.clientY } : p)
  }, [])

  // ── Состояния загрузки / ошибки ─────────────────────────────────────────────
  if (isLoading) return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-6">
      <Spinner size="sm" />
    </div>
  )
  if (error || !all?.length) return (
    <div id={anchorId} className="rounded-xl bg-white border border-gray-200 shadow-sm p-6 text-center text-sm text-gray-400">
      Нет данных для диаграммы
    </div>
  )

  const projCount          = all.filter(t => t.type === 'project').length
  const secCount           = all.filter(t => t.type === 'section').length
  const collapsedProjCount = collapsedProjs.size

  // Позиция тултипа (не выходит за правый/нижний край экрана)
  const TIP_W = 270
  const tipLeft = tip.x + 16 + TIP_W > window.innerWidth ? tip.x - TIP_W - 8 : tip.x + 16
  const tipTop  = Math.min(tip.y - 10, window.innerHeight - 200)

  // ─── Рендер ─────────────────────────────────────────────────────────────────
  return (
    <div id={anchorId} className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">

      {/* ── Тулбар ── */}
      <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {projCount} проектов · {secCount} разделов
            {collapsedProjCount > 0 && (
              <span className="ml-2 text-blue-500">({collapsedProjCount} свёрнуто)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {collapsedProjCount > 0 && (
            <button
              onClick={() => { setCollapsedProjs(new Set()); setCollapsedGroups(new Set()) }}
              className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 border border-blue-200 transition-colors"
            >
              Развернуть все
            </button>
          )}
          {collapsedProjCount < projCount && (
            <button
              onClick={() => setCollapsedProjs(new Set(all.filter(t => t.type === 'project').map(t => t.project_id)))}
              className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 border border-gray-200 transition-colors"
            >
              Свернуть все
            </button>
          )}
          <button
            onClick={scrollToToday}
            className="rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 border border-gray-200 transition-colors font-medium"
          >
            ⊙ Сегодня
          </button>
        </div>
      </div>

      {/* ── Легенда ── */}
      <div className="px-5 py-2 border-b border-gray-100">
        <p className="text-[11px] text-gray-400 mb-1">
          Цвет линии - состояние сроков: жёлтый - срок приближается, красный - есть просрочка.
        </p>
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          {([
            ['#10b981', 'В срок'],
            ['#f59e0b', 'Скоро срок (≤5 дней)'],
            ['#ef4444', 'Есть просрочка'],
            ['#9ca3af', 'Приостановлен'],
            ['#1e3a8a', 'Период проекта (в срок)'],
            ['#4f46e5', 'Группа ИРД/ПД/РД/ГГЭ (в срок)'],
          ] as const).map(([color, lbl]) => (
            <span key={lbl} className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-3 rounded-sm" style={{ background: color }} />
              {lbl}
            </span>
          ))}
          <span className="flex items-center gap-1.5 text-gray-400">
            <span className="inline-block h-3 w-0.5 bg-rose-400" />
            Сегодня
          </span>
        </div>
      </div>

      {/* ── Тело диаграммы ── */}
      <div ref={outerRef} className="overflow-auto" style={{ maxHeight: '72vh' }}>
        {/* Внутренний контейнер: ширина = левая панель + временная шкала */}
        <div style={{ width: LEFT_W + hdr.totalW }}>

          {/* ════ STICKY-ШАПКА ════ */}
          <div style={{
            position: 'sticky', top: 0, height: HDR_H, zIndex: 30,
            display: 'flex', background: '#f9fafb', borderBottom: '2px solid #e5e7eb',
          }}>
            {/* Угловая ячейка — sticky по горизонтали */}
            <div style={{
              position: 'sticky', left: 0, width: LEFT_W, flexShrink: 0, zIndex: 40,
              background: '#f9fafb', borderRight: '2px solid #e5e7eb',
              display: 'flex', alignItems: 'center', paddingLeft: 16,
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.03em' }}>
                ОБЪЕКТ / РАЗДЕЛ
              </span>
            </div>

            {/* Временная шкала шапки */}
            <div style={{ position: 'relative', width: hdr.totalW, flexShrink: 0 }}>

              {/* Ряд 1: Годы */}
              {hdr.years.map((yr, i) => (
                <div key={i} style={{
                  position: 'absolute', left: yr.x, width: yr.w,
                  top: 0, height: 24,
                  display: 'flex', alignItems: 'center', paddingLeft: 8, boxSizing: 'border-box',
                  borderRight: '1px solid #d1d5db',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#111827' }}>{yr.label}</span>
                </div>
              ))}
              <div style={{ position: 'absolute', left: 0, right: 0, top: 23, height: 1, background: '#d1d5db' }} />

              {/* Ряд 2: Месяцы */}
              {hdr.months.map((m, i) => (
                <div key={i} style={{
                  position: 'absolute', left: m.x, width: m.w,
                  top: 24, height: 24,
                  display: 'flex', alignItems: 'center', paddingLeft: 5, boxSizing: 'border-box',
                  borderRight: '1px solid #e5e7eb',
                }}>
                  <span style={{ fontSize: 11, color: '#374151', fontWeight: 500 }}>{m.label}</span>
                </div>
              ))}
              <div style={{ position: 'absolute', left: 0, right: 0, top: 47, height: 1, background: '#e5e7eb' }} />

              {/* Ряд 3: Декады */}
              {hdr.decades.map((dc, i) => (
                <div key={i} style={{
                  position: 'absolute', left: dc.x, width: dc.w,
                  top: 48, height: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
                  borderRight: '1px solid #f3f4f6',
                }}>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>{dc.label}</span>
                </div>
              ))}

              {/* Линия и подпись «Сегодня» в шапке */}
              {todayX >= 0 && todayX <= hdr.totalW && (
                <>
                  <div style={{
                    position: 'absolute', left: todayX, top: 0,
                    width: 2, height: HDR_H, background: '#ef4444', zIndex: 5,
                  }} />
                  <div style={{
                    position: 'absolute', left: todayX + 4, top: 3,
                    fontSize: 10, color: '#ef4444', whiteSpace: 'nowrap',
                    fontWeight: 700, zIndex: 5,
                  }}>
                    {todayLabel}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* ════ КОНЕЦ ШАПКИ ════ */}

          {/* ════ СТРОКИ ДАННЫХ ════ */}
          {visible.map((task, i) => {
            const isProj  = task.type === 'project'
            const isGroup = task.type === 'doc_group'
            const isSec   = task.type === 'section'
            const isStage = task.type === 'stage'

            const isProjColl    = isProj  && collapsedProjs.has(task.project_id)
            const isGroupColl   = isGroup && collapsedGroups.has(`${task.project_id}_${task.doc_type}`)
            const isSecCollapsible = isSec && sectionsWithStages.has(task.id)
            const isSecColl     = isSec && collapsedSections.has(task.id)

            const h      = isProj ? ROW_H_PROJ : isGroup ? ROW_H_GROUP : isStage ? ROW_H_STAGE : ROW_H_SEC
            const grpIdx = projGroupIdx.get(task.project_id) ?? 0
            const even   = grpIdx % 2 === 0

            const bgLeft = isProj
              ? (even ? PROJ_BG_EVEN : PROJ_BG_ODD)
              : isGroup
                ? (even ? GROUP_BG_EVEN : GROUP_BG_ODD)
                : isStage
                  ? (even ? '#f0f9ff' : '#f5f3ff')  // чуть светлее раздела
                  : (even ? SEC_BG_EVEN : SEC_BG_ODD)

            const { bX, bW, hasBar } = barGeom[i]
            const barTop = isProj ? 15 : isGroup ? 11 : isStage ? 8 : 10
            const barH   = isProj ? 16 : isGroup ? 12 : isStage ? 8 : 10

            const paddingLeft = isProj ? 10 : isGroup ? 18 : isStage ? 48 : 34

            return (
              <div
                key={task.id}
                title={task.is_mine === false ? 'Чужой проект: только просмотр' : undefined}
                style={{
                  display: 'flex', height: h, borderBottom: '1px solid #f3f4f6',
                  opacity: task.is_mine === false ? 0.5 : 1,
                }}
              >

                {/* ── Левая ячейка (sticky) ── */}
                <div
                  style={{
                    position: 'sticky', left: 0, width: LEFT_W, flexShrink: 0, zIndex: 10,
                    background: bgLeft, borderRight: '1px solid #e5e7eb',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    paddingLeft, paddingRight: 8,
                    overflow: 'hidden',
                    userSelect: 'none',
                    // Раздел: весь блок кликабелен (переход в проект)
                    cursor: isSec ? 'pointer' : isGroup ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (isGroup) toggleGroup(`${task.project_id}_${task.doc_type}`)
                    if (isSec && !isSecCollapsible) navigate(`/projects/${task.project_id}`)
                    if (isSec && isSecCollapsible)  toggleSection(task.id)
                  }}
                >
                  {isProj ? (
                    /* ─ Строка проекта: стрелка = collapse, остальное = навигация ─ */
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {/* Стрелка: только collapse/expand, не навигирует */}
                        <span
                          style={{ fontSize: 12, color: '#1e40af', flexShrink: 0, lineHeight: 1, cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); toggleProj(task.project_id) }}
                          title={isProjColl ? 'Развернуть' : 'Свернуть'}
                        >
                          {isProjColl ? '▶' : '▼'}
                        </span>
                        {/* Код + имя: клик → карточка проекта */}
                        <span
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4, flex: 1,
                            overflow: 'hidden', cursor: 'pointer',
                          }}
                          onClick={(e) => { e.stopPropagation(); navigate(`/projects/${task.project_id}`) }}
                          title={`Открыть проект: ${task.project_name}`}
                        >
                          <span style={{
                            fontSize: 11, fontWeight: 800, color: '#1e3a8a',
                            flexShrink: 0, fontFamily: 'monospace',
                          }}>
                            {task.project_code}
                          </span>
                          <span style={{
                            fontSize: 11, fontWeight: 600, color: '#1e40af',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                            textDecoration: 'underline', textDecorationColor: '#bfdbfe',
                            textUnderlineOffset: 2,
                          }}>
                            {task.project_name}
                          </span>
                          {/* Иконка перехода */}
                          <span style={{ fontSize: 9, color: '#93c5fd', flexShrink: 0, marginLeft: 2 }}>↗</span>
                        </span>
                      </div>
                      <div style={{
                        fontSize: 10, color: '#4b5563', marginTop: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        paddingLeft: 16, // отступ под стрелку
                      }}>
                        {task.gip_name ? (
                          <span style={{ color: '#6b7280' }}>ГИП: <strong style={{ color: '#374151' }}>{task.gip_name}</strong></span>
                        ) : null}
                        {isProjColl && task.start && task.end && (
                          <>
                            {task.gip_name ? <span style={{ margin: '0 4px', color: '#d1d5db' }}>·</span> : null}
                            <span>{fmtDate(task.start)} - {fmtDate(task.end)}</span>
                            <span style={{ margin: '0 4px', color: '#d1d5db' }}>·</span>
                            <span style={{ color: task.progress > 0 ? '#059669' : '#9ca3af', fontWeight: 600 }}>
                              {task.progress}%
                            </span>
                          </>
                        )}
                      </div>
                    </>
                  ) : isGroup ? (
                    /* ─ Строка группы (collapse, без навигации) ─ */
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: '#4338ca', flexShrink: 0, lineHeight: 1 }}>
                        {isGroupColl ? '▶' : '▼'}
                      </span>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: '#312e81',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {task.name}
                      </span>
                    </div>
                  ) : isStage ? (
                    /* ─ Строка этапа раздела ─ */
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                      <span style={{ fontSize: 9, color: '#9ca3af', flexShrink: 0 }}>└</span>
                      <span style={{
                        fontSize: 11, color: '#4b5563', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                      }}>
                        {task.name}
                      </span>
                      <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {fmtDate(task.start)} - {fmtDate(task.end)}
                      </span>
                    </div>
                  ) : (
                    /* ─ Строка раздела: весь блок кликабелен ─ */
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1.2 }}>
                        {/* Стрелка сворачивания — только для разделов с этапами */}
                        {isSecCollapsible && (
                          <span style={{ fontSize: 10, color: '#6b7280', flexShrink: 0, lineHeight: 1 }}>
                            {isSecColl ? '▶' : '▼'}
                          </span>
                        )}
                        {!isSecCollapsible && (
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                            background: barColor(task),
                          }} />
                        )}
                        <span style={{
                          fontSize: 12, fontWeight: 600, color: '#374151',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                          textDecoration: isSecCollapsible ? 'none' : 'underline',
                          textDecorationColor: '#d1d5db',
                          textUnderlineOffset: 2,
                        }}>
                          {task.code}
                        </span>
                        {showCost && task.cost && (
                          <span style={{ fontSize: 10, color: '#6b7280', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                            {Number(task.cost).toLocaleString('ru-RU')}
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: 10, color: '#6b7280', marginTop: 2, paddingLeft: isSecCollapsible ? 14 : 12,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2,
                      }}>
                        {task.assignee_name
                          ? <span style={{ color: '#4b5563' }}>{task.assignee_name}</span>
                          : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>-</span>
                        }
                        <span style={{ margin: '0 4px', color: '#d1d5db' }}>·</span>
                        <span>{fmtDate(task.start)} - {fmtDate(task.end)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* ── Ячейка временной шкалы ── */}
                <div
                  style={{ position: 'relative', width: hdr.totalW, flexShrink: 0, background: 'white' }}
                  onMouseMove={onBarMove}
                >
                  {/* Линия «Сегодня» */}
                  {todayX >= 0 && todayX <= hdr.totalW && (
                    <div style={{
                      position: 'absolute', left: todayX, top: 0, bottom: 0,
                      width: 1, background: '#ef4444', opacity: 0.2, pointerEvents: 'none',
                    }} />
                  )}

                  {/* Бар (не рисуем для пустой группы без дат) */}
                  {hasBar && (
                    <div
                      style={{
                        position: 'absolute', left: bX, width: bW,
                        top: barTop, height: barH,
                        background: barColor(task),
                        borderRadius: 3, cursor: 'crosshair',
                        opacity: isGroup ? 0.7 : 1,
                      }}
                      onMouseEnter={e => onBarEnter(e, task)}
                      onMouseLeave={onBarLeave}
                    />
                  )}
                </div>

              </div>
            )
          })}
          {/* ════ КОНЕЦ СТРОК ════ */}

        </div>
      </div>
      {/* ── конец тела диаграммы ── */}

      {/* ── Тултип ── */}
      {tip.visible && tip.task && (
        <div style={{
          position: 'fixed', left: tipLeft, top: tipTop, zIndex: 9999,
          background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 6px 20px rgba(0,0,0,0.13)', padding: '10px 14px',
          minWidth: 220, maxWidth: TIP_W, pointerEvents: 'none',
          fontSize: 12, lineHeight: 1.55,
        }}>
          {tip.task.type === 'stage' ? (
            /* Тултип этапа */
            <>
              <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: 12 }}>
                {tip.task.name}
              </div>
              <div style={{ color: '#6b7280' }}>
                Начало: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.start)}</strong>
              </div>
              <div style={{ color: '#6b7280' }}>
                Окончание: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.end)}</strong>
              </div>
              {showCost && tip.task.cost && (
                <div style={{ color: '#6b7280', marginTop: 4 }}>
                  Стоимость: <strong style={{ color: '#111827' }}>{Number(tip.task.cost).toLocaleString('ru-RU')} ₽</strong>
                </div>
              )}
            </>
          ) : tip.task.type === 'project' ? (
            /* Тултип проекта */
            <>
              <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 6, fontSize: 13 }}>
                {tip.task.project_code} · {tip.task.project_name}
              </div>
              {tip.task.gip_name && (
                <div style={{ color: '#6b7280' }}>
                  ГИП: <strong style={{ color: '#111827' }}>{tip.task.gip_name}</strong>
                </div>
              )}
              <div style={{ color: '#6b7280', marginTop: 5 }}>
                Начало: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.start)}</strong>
              </div>
              <div style={{ color: '#6b7280' }}>
                Окончание: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.end)}</strong>
              </div>
              <div style={{ color: '#6b7280', marginTop: 5, borderTop: '1px solid #f3f4f6', paddingTop: 5 }}>
                Готовность:{' '}
                <strong style={{ color: tip.task.progress > 0 ? '#059669' : '#9ca3af' }}>
                  {tip.task.progress}%
                </strong>
              </div>
            </>
          ) : tip.task.type === 'doc_group' ? (
            /* Тултип группы */
            <>
              <div style={{ fontWeight: 700, color: '#312e81', marginBottom: 6, fontSize: 13 }}>
                {tip.task.name}
              </div>
              <div style={{ color: '#6b7280' }}>
                Проект: <span style={{ color: '#374151' }}>{tip.task.project_name}</span>
              </div>
              {tip.task.start && tip.task.end ? (
                <>
                  <div style={{ color: '#6b7280', marginTop: 5 }}>
                    Начало: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.start)}</strong>
                  </div>
                  <div style={{ color: '#6b7280' }}>
                    Окончание: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.end)}</strong>
                  </div>
                </>
              ) : (
                <div style={{ color: '#9ca3af', marginTop: 5, fontStyle: 'italic' }}>Даты не заданы</div>
              )}
            </>
          ) : (
            /* Тултип раздела */
            <>
              <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 2, fontSize: 13 }}>
                {tip.task.name}
              </div>
              {tip.task.section_name && tip.task.section_name !== tip.task.name && (
                <div style={{ color: '#374151', marginBottom: 6, fontSize: 11 }}>
                  {tip.task.section_name}
                </div>
              )}
              <div style={{ color: '#6b7280' }}>
                Проект: <span style={{ color: '#374151' }}>{tip.task.project_name}</span>
              </div>
              {tip.task.assignee_name ? (
                <div style={{ color: '#6b7280', marginTop: 4 }}>
                  Исполнитель: <strong style={{ color: '#111827' }}>{tip.task.assignee_name}</strong>
                </div>
              ) : (
                <div style={{ color: '#9ca3af', marginTop: 4, fontStyle: 'italic' }}>
                  Исполнитель не назначен
                </div>
              )}
              <div style={{ color: '#6b7280', marginTop: 5 }}>
                Начало: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.start)}</strong>
              </div>
              <div style={{ color: '#6b7280' }}>
                Окончание: <strong style={{ color: '#111827' }}>{fmtDate(tip.task.end)}</strong>
              </div>
              {showCost && tip.task.cost && (
                <div style={{ color: '#6b7280', marginTop: 5, borderTop: '1px solid #f3f4f6', paddingTop: 5 }}>
                  Стоимость: <strong style={{ color: '#111827' }}>{Number(tip.task.cost).toLocaleString('ru-RU')}</strong>
                </div>
              )}
            </>
          )}
        </div>
      )}

    </div>
  )
}
